import { defineStore } from "pinia"
import AtividadesRealizadasRepositorio from "@/repositorios/atividades/atividades.repositorio"
import AtividadesPlanejadasRepositorio from "@/repositorios/eventos/eventos.repositorio"
import { Estados, estados } from "@/types/api"
import { usarDataHora } from "@/composable/UsarDataHora/usarDataHora"
import { usarLeitorDeDados } from "@/composable/UsarLeitorDeDados/usarLeitorDeDados"
import {
  AtividadeSimples,
  AtividadesParams,
  ProblemaAtividade,
} from "@/repositorios/atividades/atividades.modelos"
import {
  AtividadePlanejadaSimples,
  ParametrosAtividadePlanejada,
} from "@/repositorios/eventos/eventos.modelo"
import { ModeloEventoEstatisticas } from ".."
import { buscarDadosAteUltimaPagina } from "@/store/atividades/atividades.script"
import i18n from "@/i18n"

const { t } = i18n.global

const atividadesRealizadasRep = new AtividadesRealizadasRepositorio()
const atividadesPlanejadasRep = new AtividadesPlanejadasRepositorio()

const { converterParaTimeZoneLocal } = usarDataHora()
const { leitor } = usarLeitorDeDados()

const lerAtividadesRealizadas = leitor<AtividadeSimples, AtividadesParams>(
  atividadesRealizadasRep.listarAtividadesRealizadas,
  {}
)
const lerEventosPlanejados = leitor<AtividadePlanejadaSimples, ParametrosAtividadePlanejada>(
  atividadesPlanejadasRep.listarAtividadesPlanejadas,
  {}
)

interface AtividadesStore {
  atividadesRealizadas: ModeloEventoEstatisticas[]
  eventosPlanejados: ModeloEventoEstatisticas[]
  estagioDaRequisicaoDeAtividades: Estados
  estagioDaRequisicaoDeEventos: Estados
}

export const usarListarEstatisticasStore = defineStore("listar-estatisticas", {
  state: (): AtividadesStore => ({
    atividadesRealizadas: [],
    eventosPlanejados: [],
    estagioDaRequisicaoDeAtividades: estados.INICIAL,
    estagioDaRequisicaoDeEventos: estados.INICIAL,
  }),
  getters: {
    carregandoAtividades: (state) =>
      state.estagioDaRequisicaoDeAtividades === estados.PEDENTE,
    carregandoEventos: (state) => state.estagioDaRequisicaoDeEventos === estados.PEDENTE,
    atividadesComOcorrencias: (state) => {
      const atividadesRealizadas = state.atividadesRealizadas

      return atividadesRealizadas.filter(atividade => atividade.problemasJustificativas?.length)
    },
    atividadesGrafico: (state) => {
      return [...state.atividadesRealizadas, ...state.eventosPlanejados]
    },
  },
  actions: {
    definirStatus(problemas?: ProblemaAtividade[]) {
      const temProblemas = problemas && problemas.length > 0

      if (temProblemas) {
        const temAlgumaJustificativa = problemas.some((pr) => pr.justificativa)

        if (temAlgumaJustificativa) {
          return "justificado"
        }
        if (!temAlgumaJustificativa) {
          return "nao-justificado"
        }
      }

      return "normal"
    },
    mapearDTOsparaModelo(evento: AtividadeSimples): ModeloEventoEstatisticas {
      const inicio = converterParaTimeZoneLocal(evento.dataInicio, evento.horaInicio)
      const fim = converterParaTimeZoneLocal(evento.dataFim, evento.horaFim)

      return {
        id: evento.id,
        duracaoEmMinutos: evento.duracaoEmMinutos || 0,
        dataInicial: inicio,
        dataFinal: fim,
        status: this.definirStatus(evento.problemasJustificativas),
        problemasJustificativas: evento.problemasJustificativas,
      }
    },
    mapearEventosDTOsparaModelo(
      evento: AtividadePlanejadaSimples
    ): ModeloEventoEstatisticas {
      const inicio = converterParaTimeZoneLocal(evento.dataInicio, evento.horaInicio)
      const fim = converterParaTimeZoneLocal(evento.dataFim, evento.horaFim)

      return {
        id: evento.id,
        duracaoEmMinutos: evento.duracaoEmMinutos,
        dataInicial: inicio,
        dataFinal: fim,
        status: "planejado",
        problemasJustificativas: null,
      }
    },
    async verTodasAsAtividades(
      parametros: AtividadesParams = {
        "por-pagina": "1000",
      },
      leitorAtividades = lerAtividadesRealizadas
    ) {
      this.estagioDaRequisicaoDeAtividades = estados.PEDENTE

      const resolverErro = () => {
        this.atividadesRealizadas = []
        this.estagioDaRequisicaoDeAtividades = estados.ERRO
        throw new Error(t("ocorreuUmErroAoBuscarAsAtividadesRealizadas"))
      }

      leitorAtividades.sobrescreverParametros(parametros)
      try {
        await buscarDadosAteUltimaPagina(leitorAtividades)
      } catch (erro: any) {
        resolverErro()
      }

      const resultado = leitorAtividades.resultado
      if (resultado.status === "sucesso") {
        this.atividadesRealizadas = resultado.dados.map(
          this.mapearDTOsparaModelo
        )
        this.estagioDaRequisicaoDeAtividades = estados.SUCESSO
        return
      }

      if (resultado.status === "erro") {
        resolverErro()
      }

      if (resultado.status === "vazio") {
        this.atividadesRealizadas = []
        this.estagioDaRequisicaoDeAtividades = estados.SUCESSO
      }
    },
    async verEventosPlanejados(
      parametros: ParametrosAtividadePlanejada = {
        "por-pagina": "1000",
      },
      leitorEventos = lerEventosPlanejados
    ) {
      this.estagioDaRequisicaoDeEventos = estados.PEDENTE

      const resolverErro = () => {
        this.eventosPlanejados = []
        this.estagioDaRequisicaoDeEventos = estados.ERRO
        throw new Error(t("ocorreuUmErroAoBuscarOsEventosPlanejados"))
      }

      leitorEventos.sobrescreverParametros(parametros)
      try {
        await buscarDadosAteUltimaPagina(leitorEventos)
      } catch (erro: any) {
        resolverErro()
      }

      const resultado = leitorEventos.resultado

      if (resultado.status === "sucesso") {
        this.eventosPlanejados = resultado.dados.map(
          this.mapearEventosDTOsparaModelo
        )
        this.estagioDaRequisicaoDeEventos = estados.SUCESSO
        return
      }
      if (resultado.status === "erro") {
        resolverErro()
      }

      if (resultado.status === "vazio") {
        this.eventosPlanejados = []
        this.estagioDaRequisicaoDeEventos = estados.SUCESSO
      }
    },
  },
})
